import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';

const AdverseEventForm = ({ eventData, onEventDataChange, errors = {} }) => {
  const [showSeverityDetails, setShowSeverityDetails] = useState(false);

  const severityOptions = [
    { 
      value: 'mild', 
      label: 'Mild', 
      description: 'Awareness of sign/symptom, but easily tolerated' 
    },
    { 
      value: 'moderate', 
      label: 'Moderate', 
      description: 'Discomfort enough to cause interference with usual activity' 
    },
    { 
      value: 'severe', 
      label: 'Severe', 
      description: 'Incapacitating with inability to work or do usual activity' 
    },
    { 
      value: 'life_threatening', 
      label: 'Life-threatening', 
      description: 'Immediate risk of death from the reaction as it occurred' 
    },
    { 
      value: 'fatal', 
      label: 'Fatal', 
      description: 'Death caused by the adverse reaction' 
    }
  ];

  const outcomeOptions = [
    { value: 'recovered', label: 'Recovered/Resolved' },
    { value: 'recovering', label: 'Recovering/Resolving' },
    { value: 'not_recovered', label: 'Not yet recovered/resolved' },
    { value: 'recovered_sequelae', label: 'Recovered with sequelae' },
    { value: 'fatal', label: 'Fatal' },
    { value: 'unknown', label: 'Unknown' }
  ];

  const causalityOptions = [
    { value: 'certain', label: 'Certain', description: 'Clear temporal relationship, positive dechallenge' },
    { value: 'probable', label: 'Probable/Likely', description: 'Reasonable temporal relationship, likely cause' },
    { value: 'possible', label: 'Possible', description: 'Reasonable temporal relationship, uncertain cause' },
    { value: 'unlikely', label: 'Unlikely', description: 'Poor temporal relationship, other causes likely' },
    { value: 'conditional', label: 'Conditional/Unclassified', description: 'More data needed for assessment' },
    { value: 'unassessable', label: 'Unassessable/Unclassifiable', description: 'Insufficient information' }
  ];

  const handleInputChange = (field, value) => {
    onEventDataChange({
      ...eventData,
      [field]: value
    });
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'mild': return 'text-success';
      case 'moderate': return 'text-warning';
      case 'severe': return 'text-error';
      case 'life_threatening': return 'text-destructive';
      case 'fatal': return 'text-destructive';
      default: return 'text-muted-foreground';
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center space-x-3 mb-6">
        <div className="w-10 h-10 bg-error/10 rounded-lg flex items-center justify-center">
          <Icon name="AlertTriangle" size={20} className="text-error" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-foreground">Adverse Event Details</h3>
          <p className="text-sm text-muted-foreground">Comprehensive description and classification of the adverse event</p>
        </div>
      </div>
      <div className="space-y-6">
        {/* Event Onset Date */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Input
            label="Event Onset Date"
            type="date"
            value={eventData?.onsetDate || ''}
            onChange={(e) => handleInputChange('onsetDate', e?.target?.value)}
            error={errors?.onsetDate}
            required
            description="Date when adverse event first occurred"
          />

          <Input
            label="Event End Date"
            type="date"
            value={eventData?.endDate || ''}
            onChange={(e) => handleInputChange('endDate', e?.target?.value)}
            error={errors?.endDate}
            description="Leave blank if ongoing"
          />
        </div>

        {/* Event Description */}
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Event Description *
          </label>
          <textarea
            className="w-full min-h-[120px] px-3 py-2 border border-border rounded-md bg-input text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent resize-vertical"
            placeholder="Provide detailed description of the adverse event, including symptoms, timeline, and any relevant clinical findings..."
            value={eventData?.description || ''}
            onChange={(e) => handleInputChange('description', e?.target?.value)}
            required
          />
          {errors?.description && (
            <p className="text-sm text-error mt-1">{errors?.description}</p>
          )}
        </div>

        {/* Severity Assessment */}
        <div>
          <Select
            label="Severity Assessment"
            options={severityOptions}
            value={eventData?.severity || ''}
            onChange={(value) => handleInputChange('severity', value)}
            error={errors?.severity}
            required
            placeholder="Select severity level"
            description="Based on ICH E2A guidelines"
          />
          
          {eventData?.severity && (
            <div className={`mt-2 p-3 rounded-lg bg-muted/30 border-l-4 ${
              eventData?.severity === 'life_threatening' || eventData?.severity === 'fatal' 
                ? 'border-l-destructive' 
                : eventData?.severity === 'severe' ?'border-l-error' 
                : eventData?.severity === 'moderate' ?'border-l-warning' :'border-l-success'
            }`}>
              <div className="flex items-center space-x-2">
                <Icon 
                  name="Info" 
                  size={16} 
                  className={getSeverityColor(eventData?.severity)}
                />
                <span className={`text-sm font-medium ${getSeverityColor(eventData?.severity)}`}>
                  {severityOptions?.find(opt => opt?.value === eventData?.severity)?.description}
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Outcome */}
        <Select
          label="Event Outcome"
          options={outcomeOptions}
          value={eventData?.outcome || ''}
          onChange={(value) => handleInputChange('outcome', value)}
          error={errors?.outcome}
          required
          placeholder="Select event outcome"
        />

        {/* Causality Assessment */}
        <Select
          label="Causality Assessment"
          options={causalityOptions}
          value={eventData?.causality || ''}
          onChange={(value) => handleInputChange('causality', value)}
          error={errors?.causality}
          required
          placeholder="Select causality relationship"
          description="WHO-UMC causality assessment"
        />

        {/* Dechallenge/Rechallenge */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <Checkbox
              label="Dechallenge performed"
              checked={eventData?.dechallenge || false}
              onChange={(e) => handleInputChange('dechallenge', e?.target?.checked)}
              description="Drug was stopped or dose reduced"
            />
            {eventData?.dechallenge && (
              <div className="mt-3">
                <Input
                  label="Dechallenge Result"
                  type="text"
                  placeholder="Describe outcome after stopping drug"
                  value={eventData?.dechallengeResult || ''}
                  onChange={(e) => handleInputChange('dechallengeResult', e?.target?.value)}
                />
              </div>
            )}
          </div>

          <div>
            <Checkbox
              label="Rechallenge performed"
              checked={eventData?.rechallenge || false}
              onChange={(e) => handleInputChange('rechallenge', e?.target?.checked)}
              description="Drug was reintroduced after stopping"
            />
            {eventData?.rechallenge && (
              <div className="mt-3">
                <Input
                  label="Rechallenge Result"
                  type="text"
                  placeholder="Describe outcome after reintroducing drug"
                  value={eventData?.rechallengeResult || ''}
                  onChange={(e) => handleInputChange('rechallengeResult', e?.target?.value)}
                />
              </div>
            )}
          </div>
        </div>

        {/* Treatment Required */}
        <div>
          <Checkbox
            label="Treatment required for adverse event"
            checked={eventData?.treatmentRequired || false}
            onChange={(e) => handleInputChange('treatmentRequired', e?.target?.checked)}
          />
          {eventData?.treatmentRequired && (
            <div className="mt-3">
              <label className="block text-sm font-medium text-foreground mb-2">
                Treatment Details
              </label>
              <textarea
                className="w-full min-h-[80px] px-3 py-2 border border-border rounded-md bg-input text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent resize-vertical"
                placeholder="Describe treatment provided for the adverse event..."
                value={eventData?.treatmentDetails || ''}
                onChange={(e) => handleInputChange('treatmentDetails', e?.target?.value)}
              />
            </div>
          )}
        </div>

        {/* Serious Criteria */}
        {(eventData?.severity === 'severe' || eventData?.severity === 'life_threatening' || eventData?.severity === 'fatal') && (
          <div className="bg-error/5 border border-error/20 rounded-lg p-4">
            <h4 className="font-medium text-foreground mb-3 flex items-center space-x-2">
              <Icon name="AlertCircle" size={16} className="text-error" />
              <span>Serious Adverse Event Criteria</span>
            </h4>
            <div className="space-y-2">
              <Checkbox
                label="Results in death"
                checked={eventData?.criteriaDealth || false}
                onChange={(e) => handleInputChange('criteriaDealth', e?.target?.checked)}
              />
              <Checkbox
                label="Life-threatening"
                checked={eventData?.criteriaLifeThreatening || false}
                onChange={(e) => handleInputChange('criteriaLifeThreatening', e?.target?.checked)}
              />
              <Checkbox
                label="Requires hospitalization or prolongation of existing hospitalization"
                checked={eventData?.criteriaHospitalization || false}
                onChange={(e) => handleInputChange('criteriaHospitalization', e?.target?.checked)}
              />
              <Checkbox
                label="Results in persistent or significant disability/incapacity"
                checked={eventData?.criteriaDisability || false}
                onChange={(e) => handleInputChange('criteriaDisability', e?.target?.checked)}
              />
              <Checkbox
                label="Congenital anomaly/birth defect"
                checked={eventData?.criteriaCongenital || false}
                onChange={(e) => handleInputChange('criteriaCongenital', e?.target?.checked)}
              />
              <Checkbox
                label="Other medically important condition"
                checked={eventData?.criteriaMedicallyImportant || false}
                onChange={(e) => handleInputChange('criteriaMedicallyImportant', e?.target?.checked)}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdverseEventForm;